/* eslint-disable react-hooks/exhaustive-deps */
import Auth from "@aws-amplify/auth";
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from "axios";
import React from "react";
import { useHistory } from "react-router-dom";

import { useAuthContext } from "../useAuthContext";

interface RequestDefinition<Params> {
  config: AxiosRequestConfig;
  params: Params;
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const useAxios = <ResponseData, Params>(initialiser: AxiosRequestInitialiser<any>): UseAxiosReturnValue<ResponseData, Params> => {
  const { authDataState, setAuthDataState } = useAuthContext();
  const history = useHistory();

  const axiosInstance = React.useMemo(() => axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 15000,
    headers: authDataState.authenticated ? { Authorization: `Bearer ${authDataState.idToken}` } : undefined
  }), [ authDataState ]);

  const [ requestDefinition, setRequestDefinition ] = React.useState<RequestDefinition<Params> | null>(null);

  const triggerRequest = React.useCallback((params: Params, overideHeaders?: Record<string, unknown>) => setRequestDefinition({
    config: {
      ...initialiser(params),
      ...overideHeaders
    },
    params
  }), []);

  const [ response, setResponse ] = React.useState<ApiResponse<ResponseData>>({
    serverTime: null,
    data: null,
    error: null,
    pending: false,
    complete: false
  });

  const onResponseSuccess = React.useCallback((axiosResponse: AxiosResponse<ApiResponse<ResponseData>>) => {
    setResponse({
      data: axiosResponse.data.data,
      serverTime: axiosResponse.headers.date,
      error: null,
      pending: false,
      complete: true
    });
  }, []);

  const onResponseError = React.useCallback((axiosError: AxiosError<LambdaError>) => {
    if (axiosError.response) {
      if (axiosError.response.status === 403) {
        const payload = axiosError.response.data as { error?: unknown; message?: unknown };

        const details = typeof payload?.message === "string" ?
          payload.message :
          typeof payload?.error === "string" ?
            payload.error :
            "The user is not authorised to access this resource";

        const inactive = details.toLowerCase().includes("not active");

        // A disabled account cannot refresh its session. A permission error can.
        Auth.currentAuthenticatedUser({ bypassCache: true }).then(user => {
          if (inactive) {
            throw new Error("inactive");
          }

          const token = user?.signInUserSession?.getIdToken()?.getJwtToken();

          if (!token) {
            throw new Error("No ID token");
          }

          if (token !== authDataState.idToken) {
            setAuthDataState({
              authenticated: true,
              error: null,
              authenticating: false,
              fetchingUser: false,
              idToken: token
            });
            history.go(0);

            return;
          }

          setResponse({
            data: null,
            serverTime: null,
            error: {
              id: "",
              code: "UNAUTHORIZED",
              status: 403,
              title: "Unauthorised",
              details
            },
            pending: false,
            complete: true
          });
        // sign out and redirect to login if unauthenticated
        }).catch(() => {
          Auth.signOut();

          setAuthDataState({
            authenticated: false,
            fetchingUser: false,
            authenticating: false,
            error: null,
            idToken: ""
          });
        });
      } else {
        const errorMessage = axiosError?.response?.data?.error;

        // Use the given error, or provide a generic error.
        const error = {
          id: "",
          code: "CONNECTION_REFUSED",
          status: 500,
          title: "Connection Refused",
          details: errorMessage
        };

        setResponse({
          data: null,
          serverTime: null,
          error,
          pending: false,
          complete: true
        });
      }
    } else {
      setResponse({
        data: null,
        serverTime: null,
        error: {
          id: "",
          code: "CONNECTION_REFUSED",
          status: 500,
          title: "Connection Refused",
          details: "This service is unavailable. Please try again later."
        },
        pending: false,
        complete: true
      });
    }
  }, []);

  React.useEffect(() => {
    if (!requestDefinition) {
      return;
    }

    // set loading state
    setResponse({
      data: null,
      serverTime: null,
      error: null,
      pending: true,
      complete: false
    });

    // make request
    axiosInstance(requestDefinition.config)
      .then(onResponseSuccess)
      .catch(onResponseError);
  }, [ requestDefinition ]);

  return [ response, triggerRequest ];
};